import type { PmsProviderType } from "../services/pmsApi";

/**
 * UI "skin" — token-level visual presentation (colors, fonts, radius,
 * density) layered on top of Optivo's component structure. Distinct from
 * the light/dark ThemeProvider, which stays orthogonal (`.dark[data-skin=...]`).
 */
export const SKIN_IDS = ["optivo", "previo", "better_hotel", "high_contrast"] as const;
export type UiSkin = (typeof SKIN_IDS)[number];

/** Skins reachable via "auto" (provider-driven). high_contrast is excluded by construction. */
export const PROVIDER_SKINS = ["optivo", "previo", "better_hotel"] as const satisfies readonly UiSkin[];
type ProviderSkin = (typeof PROVIDER_SKINS)[number];

export const UI_SKIN_PREFERENCES = ["auto", ...SKIN_IDS] as const;
export type UiSkinPreference = (typeof UI_SKIN_PREFERENCES)[number];

export function isUiSkin(value: unknown): value is UiSkin {
    return typeof value === "string" && (SKIN_IDS as readonly string[]).includes(value);
}

export function isUiSkinPreference(value: unknown): value is UiSkinPreference {
    return value === "auto" || isUiSkin(value);
}

const PROVIDER_TO_SKIN: Record<PmsProviderType, ProviderSkin> = {
    previo: "previo",
    better_hotel: "better_hotel",
};

/** Maps the active connection's PMS provider to its matching skin. No connection → the default Optivo skin. */
export function providerToSkin(providerType: PmsProviderType | null): ProviderSkin {
    if (providerType && providerType in PROVIDER_TO_SKIN) return PROVIDER_TO_SKIN[providerType];
    return "optivo";
}

export interface ResolveSkinInput {
    preference: UiSkinPreference;
    providerType: PmsProviderType | null;
}

/** Resolves the effective skin: an explicit preference wins, "auto" follows the active PMS provider. */
export function resolveSkin({ preference, providerType }: ResolveSkinInput): UiSkin {
    if (preference === "auto") return providerToSkin(providerType);
    if (isUiSkin(preference)) return preference;
    return "optivo";
}
